import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getAllTasksApi, getSelfTasksApi } from '@/api/task'

const STORAGE_KEY = 'task-logs.visible-columns.v1'

export type TaskScope = 'self' | 'all'

export interface TaskLogsFilterState {
  scope: TaskScope
  task_id: string
  platform?: string
  action?: string
  status?: string
  channel_id?: number
  start_timestamp?: number
  end_timestamp?: number
}

export const TASK_COLUMN_KEYS = [
  'submit_time',
  'finish_time',
  'duration',
  'platform',
  'channel_id',
  'task_id',
  'username',
  'action',
  'status',
  'progress',
  'result_url',
  'fail_reason',
] as const

export type TaskColumnKey = (typeof TASK_COLUMN_KEYS)[number]

const DEFAULT_VISIBLE: TaskColumnKey[] = [
  'submit_time',
  'finish_time',
  'duration',
  'platform',
  'task_id',
  'action',
  'status',
  'progress',
  'result_url',
  'fail_reason',
]

function loadVisibleColumns(): TaskColumnKey[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULT_VISIBLE
    const arr = JSON.parse(raw)
    if (!Array.isArray(arr)) return DEFAULT_VISIBLE
    return arr.filter((x): x is TaskColumnKey =>
      (TASK_COLUMN_KEYS as readonly string[]).includes(x)
    )
  } catch {
    return DEFAULT_VISIBLE
  }
}

function buildTaskQueryParams(
  filters: TaskLogsFilterState,
  page: number,
  pageSize: number,
  isAdmin: boolean
): Record<string, string | number | undefined> {
  const params: Record<string, string | number | undefined> = {
    p: page,
    page_size: pageSize,
  }
  const tid = filters.task_id?.trim()
  if (tid) params.task_id = tid
  if (filters.platform) params.platform = filters.platform
  if (filters.action) params.action = filters.action
  if (filters.status) params.status = filters.status
  if (filters.start_timestamp) params.start_timestamp = filters.start_timestamp
  if (filters.end_timestamp) params.end_timestamp = filters.end_timestamp
  if (isAdmin && filters.scope === 'all' && filters.channel_id !== undefined && filters.channel_id > 0) {
    params.channel_id = String(filters.channel_id)
  }
  return params
}

export function useTaskLogs(isAdmin: boolean) {
  const [filters, setFilters] = useState<TaskLogsFilterState>({
    scope: 'self',
    task_id: '',
  })
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [visibleColumns, setVisibleColumnsState] =
    useState<TaskColumnKey[]>(loadVisibleColumns)

  const setVisibleColumns = (cols: TaskColumnKey[]) => {
    setVisibleColumnsState(cols)
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cols))
    } catch {
      /* ignore quota errors */
    }
  }

  const query = useQuery({
    queryKey: ['task-logs', filters, page, pageSize, isAdmin],
    queryFn: () => {
      const params = buildTaskQueryParams(filters, page, pageSize, isAdmin)
      if (filters.scope === 'self' || !isAdmin) return getSelfTasksApi(params)
      return getAllTasksApi(params)
    },
    placeholderData: (prev) => prev,
  })

  const items = query.data?.items ?? []
  const total = query.data?.total ?? 0

  const updateFilters = (patch: Partial<TaskLogsFilterState>) => {
    setFilters((prev) => ({ ...prev, ...patch }))
    setPage(1)
  }

  const resetFilters = () => {
    setFilters({
      scope: filters.scope,
      task_id: '',
      platform: undefined,
      action: undefined,
      status: undefined,
      channel_id: undefined,
      start_timestamp: undefined,
      end_timestamp: undefined,
    })
    setPage(1)
  }

  const platformOptions = useMemo(() => {
    const set = new Set<string>()
    items.forEach((x) => {
      if (x.platform) set.add(x.platform)
    })
    return Array.from(set).map((p) => ({ label: p, value: p }))
  }, [items])

  const actionOptions = useMemo(() => {
    const set = new Set<string>()
    items.forEach((x) => {
      if (x.action) set.add(x.action)
    })
    return Array.from(set).map((a) => ({ label: a, value: a }))
  }, [items])

  return {
    filters,
    updateFilters,
    resetFilters,
    page,
    pageSize,
    setPage,
    setPageSize,
    query,
    items,
    total,
    visibleColumns,
    setVisibleColumns,
    platformOptions,
    actionOptions,
  }
}
